/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React from 'react';
import { getGroup } from '@g17eco/core';
import { UniversalTrackerPlain } from '../../../types/universalTracker';
import SDGIcon from '@g17eco/molecules/sdg-icon/sdg-icon';
import { PACK, SURVEY } from '@constants/terminology';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import UniversalTrackerClassInterface from '../../../model/UniversalTrackerClassInterface';

const Label = ({ label }: { label: string }) => (
  <div className='text-label-uppercase text-medium'>{label}</div>
);

const Value = ({ value }: { value: string | number | React.JSX.Element }) => (
  <div className='mt-1'>
    {value}
  </div>
);


function getSDGIcons(sdgs: string[] | undefined) {
  if (!sdgs || sdgs.length === 0) {
    return undefined;
  }
  return <>
    {sdgs.map((sdg: string) => (
      <SimpleTooltip key={`metric-table-sdg-icon-sdgs-${sdg}`} text={`SDG ${sdg}`}>
        <SDGIcon height='30' className='mr-2' code={sdg} />
      </SimpleTooltip>
    ))}
  </>
}

const getAlternativeInfo = (alternatives: undefined | { [key: string]: { name: string } }) => {
  if (!alternatives) {
    return;
  }

  const a = [];
  for (const [key, _value] of Object.entries(alternatives)) {
    const group = getGroup('standards', key);
    if (group) {
      a.push(`${group.name}`);
    }
  }
  return <>{a.map((b, i) => <div key={`alt-info-${b}-${i}`}>{b}</div>)}</>;
}

export const TrackerInformation = (props: { utr: UniversalTrackerClassInterface }) => {
  const { utr: universalTracker } = props;

  const utr: UniversalTrackerPlain = universalTracker.getRaw();
  const columns = [
    { label: 'Metric Type', value: universalTracker.getTypeName() },
    { label: 'Type Code', value: universalTracker.getTypeCode() },
    { label: `${SURVEY.CAPITALIZED_ADJECTIVE} ${PACK.CAPITALIZED_SINGULAR}`, value: getAlternativeInfo(utr.alternatives) },
    { label: 'Related SDGs', value: getSDGIcons(utr.tags?.sdg) },
    { label: 'Metric Unit', value: universalTracker.getMetricUnitCode() },
  ]

  return (
    <div className='border-top border-ThemeNeutralsLight mt-3 p-3 row text-ThemeTextMedium text-sm'>
      {columns
        .filter((row) => !!row.value)
        .map((row) => (
          <div key={`add-info-${row.value}`} className='col-12 col-sm-6 col-md-4 my-3'>
            <Label label={row.label} />
            <Value value={row.value ?? ''} />
          </div>
        ))}
    </div>
  );
}
